import { cloneDeep } from 'lodash'
import { events$ } from '@/services'
import { EVENT_LANGUAGE, EVENT_PRODUCT } from '@/config/events'
import { BasketUtil } from '@/utils/Basket'

import useDistribution from '@/hooks/useDistribution'
import usePerson from '@/hooks/usePerson'
import useBasket from '@/hooks/useBasket'

const { getDistributionPartnerByBasket } = useDistribution()
const { hasProducts } = usePerson()
const { basket, removeUnusedDoctors, removeProductFromOvpBasket } = useBasket()

export const BasketHelper = {
  ...BasketUtil,


  /**
   *
   * @param {ProductPayload} payload
   *    @param {String} payload.productId   Id of product
   *    @param {String} payload.personId    Id of person
   *    @param {String} payload.categoryId  Id of product-category
   * @param {String} source                 Basket source
   * @return {Promise<Basket>}
   */
  removeProduct(payload, source) {
    const { productId, personId, categoryId } = payload
    return removeProductFromOvpBasket(personId, productId).then(__basket => {
      events$.emit(EVENT_PRODUCT.REMOVED, {
        basket: __basket,
        categoryId,
        personId,
        productId,
        source: source,
      })
      return basket
    })
  },

  /**
   *
   * @param {Basket} payload  Current basket
   * @return {Promise<Basket>}
   */
  removePersonsWithoutProduct(payload) {
    const personsWithoutProducts = payload.persons.filter(p => hasProducts(p.personId))
    return BasketHelper.updateBasket({ persons: personsWithoutProducts }).then(__basket => {
      return BasketHelper.newUpdateBasket(__basket)
    })
  },

  /**
   * @param {String} personId
   * @param {Basket} basket
   * @returns {{ basket: Basket, index: Integer }}
   */
  getPersonBasket(personId, basket) {
    const index = basket.persons.findIndex(p => p.personId === personId)
    return {
      basket,
      index,
    }
  },

  updateBasket(payload, shouldRemoveUnusedDoctors = false) {
    const _basket = Object.assign({}, cloneDeep(basket), payload)
    return shouldRemoveUnusedDoctors ? removeUnusedDoctors(_basket) : Promise.resolve(_basket)
  },

  updateHealthquestionsForPerson(personId, payload) {
    let { basket, index } = this.getPersonBasketFromStore(personId)
    const hqs = basket.persons[index].healthQuestionAnswers

    Object.keys(payload).forEach(hqId => {
      const hqIndex = hqs.findIndex(hq => hq.healthQuestionId === hqId)
      const hqData = basket.persons[index].healthQuestionAnswers[hqIndex]

      if (payload[hqId].doctors) {
        const doctors = payload[hqId].doctors.filter(doctor => !doctor.zsrNumber)
        BasketHelper.attachDoctors(basket, doctors)
      }

      basket.persons[index].healthQuestionAnswers[hqIndex] = Object.assign(hqData, payload[hqId])
    })

    return Promise.resolve(basket)
  },

  updateHealthquestionForPerson(personId, healthQuestionId, payload) {
    const { basket, index } = this.getPersonBasketFromStore(personId)
    const hqs = basket.persons[index].healthQuestionAnswers
    const hqIndex = hqs.findIndex(hq => hq.healthQuestionId === healthQuestionId)
    const hq = hqs[hqIndex]

    // has doctors => add to root
    if (payload.doctors) {
      const doctors = payload.doctors.filter(doctor => !doctor.zsrNumber)
      BasketHelper.attachDoctors(basket, doctors)

      delete payload.doctors
    }

    basket.persons[index].healthQuestionAnswers[hqIndex] = Object.assign({}, hq, payload)
    return Promise.resolve(basket)
  },

  /**
   * Overwrite basket language and emit event
   * @param  {Object} payload - basket
   * @return {Promise}
   */
  setLanguage(payload) {
    const language = payload.language.toLowerCase()
    events$.emit(EVENT_LANGUAGE.LOAD, { language })
    return Promise.resolve(payload)
  },
  

  /**
   * Loads the distribution partner info (e.g. for Privatassekuranzen)
   *
   * @param {BasketPayload} payload
   * @returns {Promise<Basket>}
   */
  getAndSetDistributionPartnerByBasket(payload) {
    const distributionPartnerPayload = {
      distributionPartnerNumber: getDistributionPartnerByBasket(payload),
      contractStartDate: payload.contractStartDate,
      language: payload.language,
    }

    return BasketHelper.getAndSetDistributionPartner(distributionPartnerPayload).then(() => payload)
  },

  /**
   * Checks if the basket is consistency
   *
   * FLEX-450
   * MAYBE FIX the issue that a doctor is missed or deleted after reentry in the basket.doctors array.
   *
   * @param {BasketPayload} payload
   * @param {boolean} force
   * @returns {Promise<Basket>}
   */
  checkConsistency(payload) {
    if (payload?.doctors?.length > 0) {
      payload.persons.forEach(person => {
        const products = Object.values(person.products.products)
        const product = products?.find(product => product.doctor?.zsrNumber && product.selected)
        if (product?.doctor) {
          return BasketUtil.addPersonDoctor(payload, product.doctor)
        }
      })
    }
    return Promise.resolve(payload)
  },
}

// function hasContractStartChangedAfterCancellation(payload, basket) {
//   return payload.contractStartDate !== basket.contractStartDate && !isNil(basket.cancellationAnswer)
// }
